/**
 * QuickFiltersBar Component
 * Filter controls for property search
 */

import React, { useState } from "react";
import type { PropertyFilters } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";

interface QuickFiltersBarProps {
	onFilterChange: (filters: PropertyFilters) => void;
	isLoading?: boolean;
}

const QuickFiltersBar: React.FC<QuickFiltersBarProps> = ({
	onFilterChange,
	isLoading,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [filters, setFilters] = useState<PropertyFilters>({
		property_type: undefined,
		city: undefined,
		min_rent: undefined,
		max_rent: undefined,
		bedrooms: undefined,
	});

	const handleFilterChange = (key: keyof PropertyFilters, value: any) => {
		setFilters((prev) => ({
			...prev,
			[key]: value || undefined,
		}));
	};

	const handleApplyFilters = () => {
		onFilterChange(filters);
	};

	const handleClearFilters = () => {
		const emptyFilters: PropertyFilters = {
			property_type: undefined,
			city: undefined,
			min_rent: undefined,
			max_rent: undefined,
			bedrooms: undefined,
		};
		setFilters(emptyFilters);
		onFilterChange(emptyFilters);
	};

	const hasActiveFilters = Object.values(filters).some(
		(value) => value !== undefined,
	);

	// Count active filters for mobile display
	const activeFilterCount = Object.values(filters).filter(
		(value) => value !== undefined,
	).length;

	return (
		<div className="bg-card border-y border-border sm:sticky sm:top-16 sm:z-40 backdrop-blur-sm bg-card/95">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				{/* Mobile: Collapsible Header */}
				<div className="sm:hidden">
					<button
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
						className="w-full flex items-center justify-between py-4 text-left"
					>
						<div className="flex items-center gap-2">
							<Icon name="Filter" size={20} className="text-primary" />
							<span className="font-medium text-foreground">
								{activeFilterCount > 0
									? `${activeFilterCount} Filter${
											activeFilterCount > 1 ? "s" : ""
									  } Active`
									: "Filters"}
							</span>
						</div>
						<Icon
							name={isExpanded ? "ChevronUp" : "ChevronDown"}
							size={20}
							className="text-muted-foreground"
						/>
					</button>
				</div>

				{/* Filter Form - Hidden on mobile when collapsed */}
				<div
					className={`${isExpanded ? "block" : "hidden"} sm:block py-4 sm:py-6`}
				>
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
						{/* Property Type */}
						<div>
							<Select
								label="Property Type"
								options={[
									{ value: "", label: "All Types" },
									{ value: "apartment", label: "Apartment" },
									{ value: "house", label: "House" },
									{ value: "condo", label: "Condo" },
									{ value: "townhouse", label: "Townhouse" },
									{ value: "commercial", label: "Commercial" },
								]}
								value={filters.property_type || ""}
								onValueChange={(value) =>
									handleFilterChange(
										"property_type",
										value === "" ? undefined : String(value),
									)
								}
								disabled={isLoading}
							/>
						</div>

						{/* Location */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								<Icon name="MapPin" size={14} className="inline mr-1" />
								Location
							</label>
							<Input
								type="text"
								placeholder="City, State, or Area"
								value={filters.city || ""}
								onChange={(e) => handleFilterChange("city", e.target.value)}
								disabled={isLoading}
							/>
						</div>

						{/* Min Price */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Min Price (₦)
							</label>
							<Input
								type="number"
								placeholder="Min"
								value={filters.min_rent || ""}
								onChange={(e) =>
									handleFilterChange(
										"min_rent",
										e.target.value ? parseInt(e.target.value) : undefined,
									)
								}
								disabled={isLoading}
							/>
						</div>

						{/* Max Price */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Max Price (₦)
							</label>
							<Input
								type="number"
								placeholder="Max"
								value={filters.max_rent || ""}
								onChange={(e) =>
									handleFilterChange(
										"max_rent",
										e.target.value ? parseInt(e.target.value) : undefined,
									)
								}
								disabled={isLoading}
							/>
						</div>

						{/* Bedrooms */}
						<div>
							<Select
								label="Bedrooms"
								options={[
									{ value: "", label: "Any" },
									{ value: "1", label: "1+" },
									{ value: "2", label: "2+" },
									{ value: "3", label: "3+" },
									{ value: "4", label: "4+" },
									{ value: "5", label: "5+" },
								]}
								value={filters.bedrooms?.toString() || ""}
								onValueChange={(value) =>
									handleFilterChange(
										"bedrooms",
										value === "" ? undefined : parseInt(String(value)),
									)
								}
								disabled={isLoading}
							/>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col sm:flex-row gap-3 mt-4">
						<Button
							onClick={() => {
								handleApplyFilters();
								// Auto-collapse on mobile after applying
								setIsExpanded(false);
							}}
							disabled={isLoading}
							className="flex-1 sm:flex-none"
						>
							<Icon name="Search" size={16} className="mr-2" />
							{isLoading ? "Searching..." : "Search Properties"}
						</Button>

						{hasActiveFilters && (
							<Button
								variant="outline"
								onClick={handleClearFilters}
								disabled={isLoading}
								className="flex-1 sm:flex-none"
							>
								<Icon name="X" size={16} className="mr-2" />
								Clear Filters
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default QuickFiltersBar;
